'use client';
import { createClient } from '@/lib/supabase/client';
import type { Climb, ClimbEntry, UserProfile } from '@/lib/climbData';

function isSchemaError(error: any): boolean {
  if (!error) return false;
  if (error.code && typeof error.code === 'string') {
    const cls = error.code.substring(0, 2);
    if (cls === '42' || cls === '08') return true;
    if (cls === '23') return false;
  }
  if (error.message) {
    const patterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /type.*does not exist/i,
    ];
    return patterns.some((p) => p.test(error.message));
  }
  return false;
}

// ─── Climbs ──────────────────────────────────────────────────────────────────

export const climbService = {
  async getAll(): Promise<Climb[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('climbs')
        .select('*')
        .order('name', { ascending: true });
      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return (data ?? []).map(rowToClimb);
    } catch (e: any) {
      if (isSchemaError(e)) throw e;
      return [];
    }
  },

  async upsert(climb: Climb): Promise<void> {
    const supabase = createClient();
    const { error } = await supabase.from('climbs').upsert(climbToRow(climb), { onConflict: 'id' });
    if (error) throw error;
  },

  async delete(id: string): Promise<void> {
    const supabase = createClient();
    const { error } = await supabase.from('climbs').delete().eq('id', id);
    if (error) throw error;
  },
};

// ─── Entries ─────────────────────────────────────────────────────────────────

export const entryService = {
  async getAll(): Promise<ClimbEntry[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('climb_entries')
        .select('*')
        .order('time_seconds', { ascending: true });
      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return (data ?? []).map(rowToEntry);
    } catch (e: any) {
      if (isSchemaError(e)) throw e;
      return [];
    }
  },

  async getAllForAdmin(): Promise<ClimbEntry[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('climb_entries')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return (data ?? []).map(rowToEntry);
    } catch (e: any) {
      if (isSchemaError(e)) throw e;
      return [];
    }
  },

  async updateStatus(id: string, status: ClimbEntry['status']): Promise<void> {
    const supabase = createClient();
    const { error } = await supabase
      .from('climb_entries')
      .update({ status, updated_at: new Date().toISOString() })
      .eq('id', id);
    if (error) throw error;
  },

  async flagSuspicious(id: string, suspicious: boolean, reasons: string[]): Promise<void> {
    const supabase = createClient();
    const { error } = await supabase
      .from('climb_entries')
      .update({ suspicious, suspicious_reasons: reasons, updated_at: new Date().toISOString() })
      .eq('id', id);
    if (error) throw error;
  },

  async delete(id: string): Promise<void> {
    const supabase = createClient();
    const { error } = await supabase.from('climb_entries').delete().eq('id', id);
    if (error) throw error;
  },

  async insert(entry: Omit<ClimbEntry, 'suspicious' | 'suspiciousReasons'>): Promise<void> {
    const { detectSuspicion } = await import('@/lib/suspicionDetector');
    const { suspicious, reasons } = detectSuspicion(
      entry.climbId,
      entry.timeSeconds,
      entry.wattPerKg,
      entry.date,
      entry.stravaActivityUrl,
    );
    const supabase = createClient();
    const { error } = await supabase.from('climb_entries').insert({
      id: entry.id,
      climb_id: entry.climbId,
      user_id: entry.userId,
      user_name: entry.userName,
      user_avatar: entry.userAvatar,
      strava_profile_url: entry.stravaProfileUrl,
      strava_activity_url: entry.stravaActivityUrl,
      time_seconds: entry.timeSeconds,
      time_formatted: entry.timeFormatted,
      gap_seconds: entry.gapSeconds,
      gap_formatted: entry.gapFormatted,
      watt_per_kg: entry.wattPerKg,
      date: entry.date,
      status: suspicious ? 'pending' : entry.status,
      imported_from_strava: entry.importedFromStrava,
      suspicious,
      suspicious_reasons: reasons,
    });
    if (error) throw error;
  },
};

// ─── Users ───────────────────────────────────────────────────────────────────

export const userService = {
  async getAll(): Promise<UserProfile[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .order('joined_at', { ascending: true });
      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return (data ?? []).map(rowToUser);
    } catch (e: any) {
      if (isSchemaError(e)) throw e;
      return [];
    }
  },

  async getById(id: string): Promise<UserProfile | null> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', id)
        .maybeSingle();
      if (error) {
        if (isSchemaError(error)) throw error;
        return null;
      }
      return data ? rowToUser(data) : null;
    } catch (e: any) {
      if (isSchemaError(e)) throw e;
      return null;
    }
  },

  async updateStatus(id: string, status: UserProfile['status'], clubVerified?: boolean): Promise<void> {
    const supabase = createClient();
    const updates: any = { status, updated_at: new Date().toISOString() };
    if (clubVerified !== undefined) updates.club_verified = clubVerified;
    const { error } = await supabase.from('user_profiles').update(updates).eq('id', id);
    if (error) throw error;
  },

  async delete(id: string): Promise<void> {
    const supabase = createClient();
    const { error } = await supabase.from('user_profiles').delete().eq('id', id);
    if (error) throw error;
  },
};

// ─── Settings ────────────────────────────────────────────────────────────────

export interface ClubSettings {
  clubName: string;
  stravaClubId: string;
  stravaClubUrl: string;
  autoApproveStrava: boolean;
  requireClubVerification: boolean;
  seasonName: string;
  seasonStart: string;
  seasonEnd: string;
  contactEmail: string;
}

export const settingsService = {
  async get(): Promise<ClubSettings | null> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('club_settings')
        .select('*')
        .eq('id', 1)
        .maybeSingle();
      if (error) {
        if (isSchemaError(error)) throw error;
        return null;
      }
      if (!data) return null;
      return {
        clubName: data.club_name,
        stravaClubId: data.strava_club_id,
        stravaClubUrl: data.strava_club_url,
        autoApproveStrava: data.auto_approve_strava,
        requireClubVerification: data.require_club_verification,
        seasonName: data.season_name,
        seasonStart: data.season_start,
        seasonEnd: data.season_end,
        contactEmail: data.contact_email,
      };
    } catch (e: any) {
      if (isSchemaError(e)) throw e;
      return null;
    }
  },

  async save(settings: ClubSettings): Promise<void> {
    const supabase = createClient();
    const { error } = await supabase.from('club_settings').upsert({
      id: 1,
      club_name: settings.clubName,
      strava_club_id: settings.stravaClubId,
      strava_club_url: settings.stravaClubUrl,
      auto_approve_strava: settings.autoApproveStrava,
      require_club_verification: settings.requireClubVerification,
      season_name: settings.seasonName,
      season_start: settings.seasonStart,
      season_end: settings.seasonEnd,
      contact_email: settings.contactEmail,
      updated_at: new Date().toISOString(),
    }, { onConflict: 'id' });
    if (error) throw error;
  },
};

// ─── Row mappers ─────────────────────────────────────────────────────────────

function rowToClimb(row: any): Climb {
  return {
    id: row.id,
    name: row.name,
    location: row.location,
    distance: Number(row.distance),
    elevation: row.elevation,
    avgGradient: Number(row.avg_gradient),
    maxGradient: Number(row.max_gradient),
    difficulty: row.difficulty,
    stravaSegmentId: row.strava_segment_id,
    gpxRoute: row.gpx_route ?? undefined,
    startCoords: row.start_coords ?? undefined,
    endCoords: row.end_coords ?? undefined,
  };
}

function climbToRow(climb: Climb): any {
  return {
    id: climb.id,
    name: climb.name,
    location: climb.location,
    distance: climb.distance,
    elevation: climb.elevation,
    avg_gradient: climb.avgGradient,
    max_gradient: climb.maxGradient,
    difficulty: climb.difficulty,
    strava_segment_id: climb.stravaSegmentId,
    gpx_route: climb.gpxRoute ?? null,
    start_coords: climb.startCoords ?? null,
    end_coords: climb.endCoords ?? null,
    updated_at: new Date().toISOString(),
  };
}

function rowToEntry(row: any): ClimbEntry {
  return {
    id: row.id,
    climbId: row.climb_id,
    userId: row.user_id,
    userName: row.user_name,
    userAvatar: row.user_avatar,
    stravaProfileUrl: row.strava_profile_url,
    stravaActivityUrl: row.strava_activity_url,
    timeSeconds: row.time_seconds,
    timeFormatted: row.time_formatted,
    gapSeconds: row.gap_seconds,
    gapFormatted: row.gap_formatted,
    wattPerKg: Number(row.watt_per_kg),
    date: typeof row.date === 'string' ? row.date.substring(0, 10) : row.date,
    status: row.status,
    importedFromStrava: row.imported_from_strava,
    suspicious: row.suspicious ?? false,
    suspiciousReasons: row.suspicious_reasons ?? [],
  };
}

function rowToUser(row: any): UserProfile {
  return {
    id: row.id,
    name: row.name,
    email: row.email,
    avatar: row.avatar,
    stravaId: row.strava_id ?? null,
    stravaProfileUrl: row.strava_profile_url,
    role: row.role,
    status: row.status,
    clubVerified: row.club_verified,
    joinedAt: typeof row.joined_at === 'string' ? row.joined_at.substring(0, 10) : row.joined_at,
    climbsCompleted: row.climbs_completed,
    bestRank: row.best_rank,
    authMethod: row.auth_method,
  };
}
